import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Send, XCircle, RotateCcw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function QAActionsBar({ find, onChange }) {
  const navigate = useNavigate();
  const responseId = find?.response_id || find?.public_id;
  const status = find?.qa_status;
  const round = Number(find?.revision_round || 0);
  const maxRounds = Number(find?.revision_max_rounds || 2);

  const sat = find?.satisaction || {};
  const hasSatisaction = (sat.strengths || []).length > 0 || (sat.opportunities || []).length > 0;

  const activeFlags = (find?.revision_flags || []).filter(
    (f) => !f.resolved && Number(f.round || 0) === round + 1,
  );

  const canPublishOrReject = ['in_review', 'revised', 'revision_requested'].includes(status);
  const canRequestRevision = canPublishOrReject && activeFlags.length > 0 && round < maxRounds;
  const reachedMaxRounds = round >= maxRounds;

  const [publishConfirm, setPublishConfirm] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [revisionConfirm, setRevisionConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const publish = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/qa/finds/${responseId}/publish`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) { toast.error(data?.detail || 'Publish failed'); return; }
      toast.success('FIND published');
      onChange({ qa_status: 'published', published_at: data.published_at });
      setPublishConfirm(false);
      navigate('/qa-queue');
    } catch { toast.error('Publish failed'); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (rejectReason.trim().length < 10) { toast.error('Reason must be at least 10 characters'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/qa/finds/${responseId}/reject`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data?.detail || 'Reject failed'); return; }
      toast.success('FIND rejected');
      onChange({ qa_status: 'rejected', rejection_reason: rejectReason.trim() });
      setRejectOpen(false);
      navigate('/qa-queue');
    } catch { toast.error('Reject failed'); }
    finally { setBusy(false); }
  };

  const requestRevision = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/qa/finds/${responseId}/request-revision`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) { toast.error(data?.detail || 'Request revision failed'); return; }
      toast.success(`Revision request sent · Round ${data.revision_round}/${data.revision_max_rounds}${data.email_sent ? ' · email delivered' : ''}`);
      onChange({ qa_status: 'revision_requested', revision_round: data.revision_round });
      setRevisionConfirm(false);
      navigate('/qa-queue');
    } catch { toast.error('Request revision failed'); }
    finally { setBusy(false); }
  };

  if (status === 'published') {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3" data-testid="qa-actions-published">
        <CheckCircle2 className="w-5 h-5 text-emerald-700" />
        <div>
          <p className="text-sm font-semibold text-emerald-900">Published</p>
          <p className="text-xs text-emerald-700">This FIND has been delivered to the client. To make changes, contact a platform admin.</p>
        </div>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-center gap-3" data-testid="qa-actions-rejected">
        <XCircle className="w-5 h-5 text-rose-700" />
        <div>
          <p className="text-sm font-semibold text-rose-900">Rejected</p>
          {find?.rejection_reason && <p className="text-xs text-rose-700 italic">Reason: {find.rejection_reason}</p>}
        </div>
      </div>
    );
  }
  if (status === 'revision_requested') {
    return (
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-center gap-3" data-testid="qa-actions-awaiting-revision">
        <RotateCcw className="w-5 h-5 text-purple-700" />
        <div>
          <p className="text-sm font-semibold text-purple-900">Awaiting satisFINDER<sup>®</sup> revision · Round {round}/{maxRounds}</p>
          <p className="text-xs text-purple-700">The satisFINDER<sup>®</sup> has been emailed and can re-submit the flagged questions.</p>
        </div>
      </div>
    );
  }

  return (
    <section className="bg-white rounded-xl border border-[#E2E8F0] p-4 space-y-3" data-testid="qa-actions-bar">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-[#0F172A]">Final actions</p>
          <p className="text-xs text-[#64748B]">
            Round {round + 1} of {maxRounds}
            {reachedMaxRounds && ' — final round, must publish or reject'}
            {activeFlags.length > 0 && ` · ${activeFlags.length} question${activeFlags.length === 1 ? '' : 's'} flagged`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setRevisionConfirm(true)}
            disabled={!canRequestRevision || busy}
            title={!canRequestRevision ? (reachedMaxRounds ? 'Max rounds reached' : 'Flag at least one question first') : ''}
            className="border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-50"
            data-testid="qa-request-revision-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" />
            Request Revision
          </Button>
          <Button
            variant="outline"
            onClick={() => setRejectOpen(true)}
            disabled={!canPublishOrReject || busy}
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid="qa-reject-btn"
          >
            <XCircle className="w-4 h-4 mr-1.5" /> Reject
          </Button>
          <Button
            onClick={() => setPublishConfirm(true)}
            disabled={!canPublishOrReject || busy || !hasSatisaction}
            title={!hasSatisaction ? 'Add satisACTION® bullets before publishing' : ''}
            className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
            data-testid="qa-publish-btn"
          >
            <Send className="w-4 h-4 mr-1.5" /> Publish
          </Button>
        </div>
      </div>
      {!hasSatisaction && canPublishOrReject && (
        <p className="text-xs text-amber-700 inline-flex items-center gap-1.5" data-testid="qa-publish-blocked-hint">
          <AlertTriangle className="w-3.5 h-3.5" /> satisACTION® is empty — add at least one strength or opportunity before publishing.
        </p>
      )}

      {/* Publish confirm */}
      <Dialog open={publishConfirm} onOpenChange={(v) => !busy && setPublishConfirm(v)}>
        <DialogContent className="sm:max-w-[480px]" data-testid="qa-publish-modal">
          <DialogHeader>
            <DialogTitle className="font-['Montserrat'] flex items-center gap-2"><Send className="w-4 h-4 text-emerald-600" /> Publish this FIND?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#475569]">
            <p>The satisFINDER<sup>®</sup> will receive an email with their rating and your QA comment. payment_eligible will be set to <span className="font-semibold">true</span>.</p>
            <p className="text-xs text-[#94A3B8]">Once published, edits require platform admin intervention.</p>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setPublishConfirm(false)} disabled={busy}>Cancel</Button>
            <Button onClick={publish} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="qa-publish-confirm">
              {busy ? 'Publishing…' : 'Confirm publish'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject modal */}
      <Dialog open={rejectOpen} onOpenChange={(v) => !busy && setRejectOpen(v)}>
        <DialogContent className="sm:max-w-[500px]" data-testid="qa-reject-modal">
          <DialogHeader>
            <DialogTitle className="font-['Montserrat'] flex items-center gap-2"><XCircle className="w-4 h-4 text-rose-600" /> Reject FIND</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-1">
            <p className="text-sm text-[#475569]">The FIND will be archived and the satisFINDER<sup>®</sup> will be emailed the reason. <span className="font-semibold">Minimum 10 characters required.</span></p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              placeholder="Why is this FIND being rejected?"
              data-testid="qa-reject-reason-input"
            />
            <p className={`text-xs ${rejectReason.trim().length >= 10 ? 'text-emerald-600' : 'text-[#94A3B8]'}`}>{rejectReason.trim().length} / 10</p>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={reject} disabled={busy || rejectReason.trim().length < 10} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="qa-reject-confirm">
              {busy ? 'Rejecting…' : 'Confirm reject'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Revision confirm */}
      <Dialog open={revisionConfirm} onOpenChange={(v) => !busy && setRevisionConfirm(v)}>
        <DialogContent className="sm:max-w-[480px]" data-testid="qa-revision-modal">
          <DialogHeader>
            <DialogTitle className="font-['Montserrat'] flex items-center gap-2"><RotateCcw className="w-4 h-4 text-purple-600" /> Request Revision · Round {round + 1}/{maxRounds}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-[#475569]">
            <p>The satisFINDER<sup>®</sup> will receive an email listing the {activeFlags.length} flagged question{activeFlags.length === 1 ? '' : 's'} and your notes. They can re-submit only the flagged answers.</p>
            <p className="text-xs text-[#94A3B8]">This counts as a revision round and increments the round counter.</p>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setRevisionConfirm(false)} disabled={busy}>Cancel</Button>
            <Button onClick={requestRevision} disabled={busy} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="qa-revision-confirm">
              {busy ? 'Sending…' : 'Send revision request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
